/**
 * Mirrors Angular `TransferOptionComponent` (transfer-option.component).
 * Policy-gated options match `transfer-option.component.html` (`hasPolicy` per row).
 * Modal opens with no option selected (user must choose explicitly).
 *
 * UI: option cards with title + description and a checkbox (no colored indicator dots).
 * Header/footer use the shared OnScreenModal grey-blue shell.
 */
import { useEffect, useRef, useState } from "react"
import { OnScreenModal } from "@/components/ui/OnScreenModal"
import modalStyles from "@/features/application/styles/modals.module.css"
import { useUserRole } from "@/hooks/useUserRole"
import { getTransferProcessLog } from "../api/transferApi"
import type { TransferOptionModalProps, TransferOptionValue } from "../types"

const WIZARD_TRANSFER_OPTIONS = [
  "transfer_property",
  "transfer_resident",
  "merge_property",
  "split_property",
] as const satisfies readonly TransferOptionValue[]

const TRANSFER_OPTIONS: Array<{
  id: TransferOptionValue
  title: string
  policy: string
}> = [
  {
    id: "transfer_property",
    title: "Transfer Property to a New Company",
    policy: "transfer_property",
  },
  {
    id: "transfer_staff",
    title: "Transfer Staff from One Property to Another",
    policy: "transfer_staff",
  },
  {
    id: "transfer_resident",
    title: "Transfer Residents from One Property to Another",
    policy: "transfer_resident",
  },
  {
    id: "merge_property",
    title: "Merge Two Properties under the Same Company",
    policy: "merge_property",
  },
  {
    id: "split_property",
    title: "Split One Property into two properties",
    policy: "split_property",
  },
]

function isWizardTransferOption(value: string): value is Exclude<TransferOptionValue, "transfer_staff"> {
  return (WIZARD_TRANSFER_OPTIONS as readonly string[]).includes(value)
}

/**
 * Property Edit header "Transfer" OR-list. Includes `split_property` so users with only
 * that policy can open the modal (row still gated by its own policy inside the modal).
 */
export const PROPERTY_EDIT_TRANSFER_HEADER_POLICIES = [
  "transfer_resident",
  "transfer_staff",
  "transfer_property",
  "merge_property",
  "split_property",
] as const

export function TransferOptionModal({
  isOpen,
  propertyId,
  onClose,
  onProceedWizard,
  onProceedStaffTransfer,
}: TransferOptionModalProps) {
  const { hasPolicy } = useUserRole()
  const [option, setOption] = useState<TransferOptionValue | "">("")
  /** Prevents the pending-log fetch from overwriting a choice the user already made. */
  const userPickedOptionRef = useRef(false)

  useEffect(() => {
    userPickedOptionRef.current = false
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset the selected option when the modal opens or the property changes
    setOption("")
  }, [isOpen, propertyId])

  useEffect(() => {
    if (!isOpen || !propertyId) return
    let cancelled = false
    void (async () => {
      try {
        const rows = await getTransferProcessLog({
          property_id: propertyId,
          status: "Pending",
        })
        // User may have clicked an option while this request was in flight — keep their choice.
        if (cancelled || userPickedOptionRef.current) return
        const pendingName = rows[0]?.process_name
        if (pendingName && isWizardTransferOption(pendingName)) {
          setOption(pendingName)
        }
      } catch {
        /* non-blocking — user can still pick an option */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, propertyId])

  const selectOption = (id: TransferOptionValue) => {
    userPickedOptionRef.current = true
    setOption((prev) => (prev === id ? "" : id))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!option) return
    if (option === "transfer_staff") {
      onProceedStaffTransfer()
      onClose()
      return
    }
    onProceedWizard(option)
    onClose()
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Transfer Process"
      onClose={onClose}
      maxWidth="max-w-xl"
      overlayDataTestId="transfer-option-modal-overlay"
      panelDataTestId="transfer-option-modal-panel"
      titleDataTestId="transfer-option-modal-title"
      closeButtonDataTestId="transfer-option-modal-close-button"
      footer={
        <div className={modalStyles.modalFooterActionGroup}>
          <button
            type="button"
            onClick={onClose}
            data-testid="transfer-option-cancel-button"
            className={modalStyles.modalActionSecondary}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="transfer-option-form"
            disabled={!option}
            data-testid="transfer-option-proceed-button"
            className={modalStyles.modalActionPrimary}
          >
            Proceed
          </button>
        </div>
      }
    >
      <form
        id="transfer-option-form"
        data-testid="transfer-option-form"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <p
          data-testid="transfer-option-form-description"
          className="m-0 text-center text-sm text-slate-500"
        >
          Select the transfer process option to continue.
        </p>

        <div data-testid="transfer-option-checkbox-group" className="space-y-2.5" role="group">
          {TRANSFER_OPTIONS.map(({ id, title, policy }) => {
            if (!hasPolicy([policy])) return null
            const checked = option === id
            return (
              <label
                key={id}
                data-testid={`transfer-option-checkbox-label-${id}`}
                className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors ${
                  checked
                    ? "border-[rgb(21_151_255)] bg-[rgb(21_151_255)]/5 shadow-sm"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <span
                  data-testid={`transfer-option-checkbox-text-${id}`}
                  className="min-w-0 flex-1 text-[15px] font-semibold leading-snug text-slate-900"
                >
                  {title}
                </span>
                <input
                  type="checkbox"
                  name="transfer_option"
                  value={id}
                  checked={checked}
                  onChange={() => selectOption(id)}
                  className="h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-[rgb(21_151_255)] accent-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                  data-testid={`transfer-option-checkbox-${id}`}
                />
              </label>
            )
          })}
        </div>
      </form>
    </OnScreenModal>
  )
}
